// ...existing header components replaced by CustomPageHeader
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
// axios and apiConfig not needed; using service helpers instead
import {
  cancelSubscriptionStripe,
  createSubscriptionUrl,
  getSubscriptionStripeHistory,
  getSubscriptionStripeStatus,
} from "../services/userService";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import Loader from "../components/Loader";
import ConfirmDialog from "../components/ConfirmDialog";
import { toast } from "react-toastify";

const SubscriptionScreen = ({ userId, token }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleBack = () => {
    // Check if we came from Settings page
    const fromSettings = location.state?.from === 'settings' || 
                         document.referrer.includes('/settings');
    
    if (fromSettings) {
      navigate('/settings');
    } else {
      navigate('/home');
    }
  };
  // status is no longer used; we track subscription via isSubscribedState
  const [loading, setLoading] = useState(false);
  const [expiry, setExpiry] = useState(null);
  // defer initial selection until we know the user's subscription to avoid UI flicker
  // when the page first loads. After checking subscription status we'll set this
  // to the active plan (if any) or default to 'monthly'.
  const [selectedPlan, setSelectedPlan] = useState("trial");
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPlanForSubscription, setSelectedPlanForSubscription] =
    useState(null);

  const handleSubsctiption = async (plan) => {
    // Don't allow subscription to trial plan
    if (plan === "trial") {
      toast.error(
        t("cannot_subscribe_to_trial", "Cannot subscribe to trial plan")
      );
      return;
    }

    try {
      setSubscribing(true);
      const res = await createSubscriptionUrl({ plan_type: plan });

      if (res?.success && res?.data?.checkout_url) {
        const url = res.data.checkout_url;
        // Redirect user to checkout URL open in a new tab
        window.open(url, "_blank");
      } else {
        // Show toast message from API or default message
        const errorMessage =
          res?.message ||
          res?.data?.message ||
          t("subscription_creation_failed", "Subscription creation failed");
        toast.success(errorMessage);
        // 
      }
    } catch (err) {
      // Error handling is done by centralized error handler in axios interceptor
      // Only handle specific cases here if needed
    } finally {
      setSubscribing(false);
    }
  };

  const getActionType = (newPlan) => {
    if (!subscriptionDetails) return "subscribe";

    const expiryDate = parseDateStr(
      subscriptionDetails.expiry_date ||
        subscriptionDetails.expiry_date_without_time
    );
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isExpired = expiryDate ? expiryDate < today : false;

    // Check if subscription is active based on product_name and not expired
    const isActive =
      !isExpired &&
      (subscriptionDetails.product_name === "free_tril" ||
        subscriptionDetails.product_name === "is_monthly" ||
        subscriptionDetails.product_name === "monthly_plan" ||
        subscriptionDetails.product_name === "is_yearly" ||
        subscriptionDetails.product_name === "yearly_plan");

    if (!isActive) return "subscribe";

    // If currently on free trial, any paid plan selection is an upgrade
    if (subscriptionDetails.product_name === "free_tril") {
      if (newPlan === "trial") return "active"; // Already on trial
      return "subscribe"; // From trial to paid plan
    }

    if (
      (subscriptionDetails.product_name === "is_yearly" ||
        subscriptionDetails.product_name === "yearly_plan") &&
      newPlan === "monthly"
    )
      return "downgrade";
    if (
      (subscriptionDetails.product_name === "is_monthly" ||
        subscriptionDetails.product_name === "monthly_plan") &&
      newPlan === "yearly"
    )
      return "upgrade";

    return "change";
  };

  const handleSubscribeClick = (plan) => {
    // Don't allow subscribing to trial if it's already active
    if (plan === "trial" && isSelectedPlanActive()) {
      return;
    }
    setSelectedPlanForSubscription(plan);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubscription = () => {
    setShowConfirmDialog(false);
    if (selectedPlanForSubscription) {
      handleSubsctiption(selectedPlanForSubscription);
      setSelectedPlanForSubscription(null);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmDialog(false);
    setSelectedPlanForSubscription(null);
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getSubscriptionStripeStatus()
      .then((res) => {
        if (!mounted) return;
        const d = (res.data && res.data) || {};

        // prefer date-only field if present
        const expiryRaw = d.expiry_date_without_time || d.expiry_date || null;
        const expiryDate = parseDateStr(expiryRaw);

        // compare to today's date at midnight
        const now = new Date();
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const isExpired = expiryDate ? expiryDate < today : false;

        // store details and set derived flags
        setSubscriptionDetails(d);
        // active status is derived from subscriptionDetails and expiry; stored in subscriptionDetails above
        setExpiry(expiryRaw || null);

        // auto-select current plan based on product_name, otherwise pick a sensible default
        if (!isExpired) {
          if (d.product_name === "free_tril") setSelectedPlan("trial");
          else if (
            d.product_name === "is_monthly" ||
            d.product_name === "monthly_plan"
          )
            setSelectedPlan("monthly");
          else if (
            d.product_name === "is_yearly" ||
            d.product_name === "yearly_plan"
          )
            setSelectedPlan("yearly");
          else setSelectedPlan("trial"); // default to trial
        } else {
          // expired subscription — allow the user to choose; default to monthly
          setSelectedPlan("monthly");
        }
      })
      .catch((err) => {
        // Error fetching subscription plans
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const formatExpiry = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Standardize to MM/DD/YYYY as requested
      return dayjs(dateStr).format("MM/DD/YYYY");
    } catch (e) {
      return dateStr;
    }
  };

  const formatExpiryTrial = (dateStr) => {
    // Use dayjs to parse and format as MM/DD/YYYY for consistency
    if (!dateStr) return null;
    try {
      return dayjs(dateStr).format("MM/DD/YYYY");
    } catch (e) {
      return dateStr;
    }
  };

  // parse a date string from API into a local Date using dayjs
  const parseDateStr = (dateStr) => {
    if (!dateStr) return null;
    try {
      const parsed = dayjs(dateStr);
      return parsed.isValid() ? parsed.toDate() : null;
    } catch (e) {
      return null;
    }
  };

  // Whether the selected plan is the user's currently active plan (and not expired).
  const isSelectedPlanActive = () => {
    if (!subscriptionDetails) return false;
    const expiryDate = parseDateStr(
      subscriptionDetails.expiry_date ||
        subscriptionDetails.expiry_date_without_time
    );
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isExpired = expiryDate ? expiryDate < today : false;
    if (isExpired) return false;

    // Check if subscription is active based on product_name
    const isActive =
      subscriptionDetails.product_name === "free_tril" ||
      subscriptionDetails.product_name === "is_monthly" ||
      subscriptionDetails.product_name === "monthly_plan" ||
      subscriptionDetails.product_name === "is_yearly" ||
      subscriptionDetails.product_name === "yearly_plan";
    if (!isActive) return false;

    if (
      selectedPlan === "trial" &&
      subscriptionDetails.product_name === "free_tril"
    )
      return true;
    if (
      selectedPlan === "monthly" &&
      (subscriptionDetails.product_name === "is_monthly" ||
        subscriptionDetails.product_name === "monthly_plan")
    )
      return true;
    if (
      selectedPlan === "yearly" &&
      (subscriptionDetails.product_name === "is_yearly" ||
        subscriptionDetails.product_name === "yearly_plan")
    )
      return true;
    return false;
  };

  const termsUrl = "https://vibelinksapp.com/terms-of-service/";
  const privacyUrl = "https://vibelinksapp.com/privacy-policy/";
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const dotsRef = useRef(null);

  // close dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (dotsRef.current && !dotsRef.current.contains(e.target)) {
        setShowDotsMenu(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleHistory = () => {
    // Navigate to subscription history page or open modal
    getSubscriptionStripeHistory().then((res) => {
      if (res?.data?.portal_url) {
        window.open(res?.data?.portal_url, "_blank", "noopener,noreferrer");
      } else {
        toast.error(res?.message);
      }
      // window.location.href = "/subscription/history";
    });
  };

  const handleCancelActiveSubscription = async () => {
    // Call cancel subscription API (implement or wire to service)
    try {
      setLoading(true);
      cancelSubscriptionStripe().then((res) => {
        if (res?.success) {
          toast.success(res?.message || t("cancel_success", "Cancelled"));
          // Update local state: clear subscription details so UI shows unsubscribed
          setSubscriptionDetails(null);
        } else {
          toast.error(res?.message || t("cancel_failed", "Cancel failed"));
        }
      });
    } catch (err) {
      toast.error(
        t("subscription_cancel_failed", "Failed to cancel subscription")
      );
    } finally {
      setLoading(false);
      setShowDotsMenu(false);
    }
  };


  return (
    <>
      <CustomPageHeader
        onBack={handleBack}
        // hideBack={true}
        // showPageHeader={false}
        pageContainerClass=""
        title={t("subscription", "Vibelinks")}
      >
        <div className="max-w-lg mx-auto p-6">
          {loading && (
            <div className="py-8">
              <Loader />
            </div>
          )}
          {!loading && (
            <>
              <div
                className="relative w-full inline-block flex justify-end"
                ref={dotsRef}
              >
                {
                  <button
                    className="p-2 rounded-full bg-gray-100 dark:bg-[#252739] shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDotsMenu((s) => !s);
                    }}
                    aria-haspopup="true"
                    aria-expanded={showDotsMenu}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-gray-600 dark:text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v.01M12 12v.01M12 18v.01"
                      />
                    </svg>
                  </button>
                }

                {showDotsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#252739] border dark:border-[#1a1d35] rounded-md shadow-lg z-10">
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#151729] text-black dark:text-white"
                      onClick={() => handleHistory()}
                    >
                      {t("history", "History")}
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#151729] text-black dark:text-white"
                      onClick={() => handleCancelActiveSubscription()}
                    >
                      {t("cancel", "Cancel")}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-center text-gray-700 dark:text-dark-text mb-6">
                {t(
                  "subscription_intro",
                  "By subscribing, you unlock a full suite of features, can create Vibes and invite your links."
                )}
              </p>

              <p className="text-center text-gray-700 dark:text-dark-text mb-6">
                {t(
                  "subscription_renew_info",
                  "You will be charged via your Stripe account, and your subscription will automatically renew each month for the same amount unless you cancel it in your Stripe settings at least 24 hours before the current period ends."
                )}
              </p>

              <p className="text-center text-gray-700 dark:text-dark-text mb-6">
                {t(
                  "subscription_agree",
                  "By tapping subscribe you agree to our"
                )}{" "}
                <Link
                  to={privacyUrl}
                  target="_blank"
                  className="text-[#6d29f6] underline"
                >
                  {t("privacy_policy", "Privacy Policy")}
                </Link>{" "}
                {t("and", "and")}{" "}
                <Link
                  to={termsUrl}
                  target="_blank"
                  className="text-[#6d29f6] underline"
                >
                  {t("terms_of_use", "Terms of Use")}
                </Link>
                .
              </p>

              <div className="space-y-4">
                {/* Display free trial button only when product_name is "free_tril" */}

                <div
                  className={`w-full rounded-xl p-4 flex justify-between items-center shadow cursor-pointer ${(() => {
                    // Check if free trial is selected or active
                    const isSelected = selectedPlan === "trial";
                    const expiryDate = parseDateStr(
                      subscriptionDetails?.expiry_date
                    );
                    const now = new Date();
                    const today = new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate()
                    );
                    const isActive = expiryDate ? expiryDate >= today : false;

                    if (isSelected) {
                      return "bg-[#6d29f6] text-white";
                    } else {
                      return isActive
                        ? "bg-white dark:bg-[#252739] text-gray-700 dark:text-white border-2 border-[#6d29f6]"
                        : "bg-white dark:bg-[#252739] text-gray-700 dark:text-white border-2 border-gray-200 dark:border-[#1a1d35]";
                    }
                  })()}`}
                  onClick={() => !loading && setSelectedPlan("trial")}
                  role="button"
                  aria-pressed={selectedPlan === "trial"}
                >
                  <div className="font-medium">
                    {t("free_trial", "Free Trial")}
                  </div>
                  <div className="text-sm">
                    {(() => {
                      // Check if free trial is active based only on expiry date
                      const expiryDate = parseDateStr(
                        subscriptionDetails?.expiry_date
                      );
                      const now = new Date();
                      const today = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate()
                      );
                      const isExpired = expiryDate ? expiryDate < today : false;

                      return !isExpired
                        ? t("one_month_unlimited", "1 Month Unlimited Vibes")
                        : t("trial_expired", "has expired");
                    })()}
                  </div>
                </div>

                {(() => {
                  const plan = "monthly";
                  // subscribed if subscriptionDetails indicates monthly active and not expired
                  const expiryDate = parseDateStr(
                    subscriptionDetails?.expiry_date ||
                      subscriptionDetails?.expiry_date_without_time
                  );
                  const now = new Date();
                  const today = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate()
                  );
                  const isExpired = expiryDate ? expiryDate < today : false;
                  const subscribed =
                    !isExpired &&
                    (subscriptionDetails?.product_name === "is_monthly" ||
                      subscriptionDetails?.product_name === "monthly_plan");
                  const highlighted = selectedPlan
                    ? selectedPlan === plan
                    : subscribed;

                  return (
                    <div
                      className={`w-full rounded-xl p-4 flex justify-between items-center shadow ${
                        highlighted
                          ? "bg-[#6d29f6] text-white cursor-pointer"
                          : "bg-white dark:bg-[#252739] text-gray-700 dark:text-white border-2 border-[#6d29f6] cursor-pointer"
                      }`}
                      onClick={() => !loading && setSelectedPlan(plan)}
                      role="button"
                      aria-pressed={selectedPlan === plan}
                    >
                      <span className="text-lg font-medium">
                        {t("monthly_price", "Rs 280.00 / Month")}
                      </span>
                      <span className="text-sm">
                        {t("unlimited", "Unlimited")}
                      </span>
                    </div>
                  );
                })()}

                {(() => {
                  const plan = "yearly";
                  const expiryDate = parseDateStr(
                    subscriptionDetails?.expiry_date ||
                      subscriptionDetails?.expiry_date_without_time
                  );
                  const now = new Date();
                  const today = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate()
                  );
                  const isExpired = expiryDate ? expiryDate < today : false;
                  const subscribed =
                    !isExpired &&
                    (subscriptionDetails?.product_name === "is_yearly" ||
                      subscriptionDetails?.product_name === "yearly_plan");
                  const highlighted = selectedPlan
                    ? selectedPlan === plan
                    : subscribed;

                  return (
                    <div
                      className={`w-full rounded-xl p-4 flex justify-between items-center shadow ${
                        highlighted
                          ? "bg-[#6d29f6] text-white cursor-pointer"
                          : "bg-white dark:bg-[#252739] text-gray-700 dark:text-white border-2 border-[#6d29f6] cursor-pointer"
                      }`}
                      onClick={() => !loading && setSelectedPlan(plan)}
                      role="button"
                      aria-pressed={selectedPlan === plan}
                    >
                      <div className="text-lg font-medium">
                        {t("yearly_price", "Rs 2,800.00 / Year")}
                      </div>
                      <div className="text-sm">
                        {t("unlimited", "Unlimited")}
                      </div>
                    </div>
                  );
                })()}

                {expiry && (() => {
                  // Check if subscription is expired
                  const expiryDate = parseDateStr(expiry);
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const isExpired = expiryDate ? expiryDate < today : false;

                  return (
                    <div className="text-center text-gray-500 dark:text-dark-text-secondary text-sm">
                      {subscriptionDetails?.product_name === "free_tril"
                        ? isExpired
                          ? `${t(
                              "Your_free_trial_period_has_expired_on",
                              "Your free trial period has been expired on"
                            )} ${formatExpiryTrial(expiry)}`
                          : `${t(
                              "Your_free_trial_period_expires_on",
                              "Your free trial period expires on"
                            )} ${formatExpiryTrial(expiry)}`
                        : isExpired
                        ? `${t(
                            "subscription_expired",
                            "Your subscription has been expired on"
                          )} ${formatExpiry(expiry)}`
                        : `${t(
                            "subscription_expiry",
                            "Your subscription will expire on"
                          )} ${formatExpiry(expiry)}`}
                    </div>
                  );
                })()}

                {/* Main action button: show 'Subscribed' only when selected plan matches active subscription */}
                {(() => {
                  const isTrialSelected = selectedPlan === "trial";
                  const isDisabled =
                    loading ||
                    subscribing ||
                    isSelectedPlanActive() ||
                    isTrialSelected;
                  const disabledStyles =
                    "bg-gray-300 text-gray-500 cursor-not-allowed";
                  const enabledStyles = "bg-[#6d29f6] text-white";
                  return (
                    <button
                      className={`w-full ${
                        isDisabled ? disabledStyles : enabledStyles
                      } rounded-xl p-4 shadow-lg flex items-center justify-center`}
                      onClick={() =>
                        !isSelectedPlanActive() &&
                        !isTrialSelected &&
                        handleSubscribeClick(selectedPlan)
                      }
                      disabled={isDisabled}
                    >
                      {isSelectedPlanActive() ? (
                        selectedPlan === "trial" ? (
                          t("subscribed", "Active Trial")
                        ) : (
                          t("subscribed", "Subscribed")
                        )
                      ) : subscribing ? (
                        <>
                          <Loader size={16} loaderColor="#ffffff" />
                          <span className="ml-2">
                            {(() => {
                              const actionType = getActionType(selectedPlan);
                              switch (actionType) {
                                case "upgrade":
                                  return t("subscribe", "Upgrading...");
                                case "downgrade":
                                  return t("subscribe", "Downgrading...");
                                default:
                                  return t("subscribe", "Subscribing...");
                              }
                            })()}
                          </span>
                        </>
                      ) : (
                        (() => {
                          if (isTrialSelected) {
                            // Optional: clarify that trial cannot be subscribed via checkout
                            return t("subscribe", "Trial Selected");
                          }
                          const actionType = getActionType(selectedPlan);
                          switch (actionType) {
                            case "active":
                              return selectedPlan === "trial"
                                ? t("subscribe", "Active Trial")
                                : t("subscribe", "Active");
                            case "upgrade":
                              return t("subscribe", "Upgrade");
                            case "downgrade":
                              return t("subscribe", "Downgrade");
                            default:
                              return t("subscribe", "Subscribe");
                          }
                        })()
                      )}
                    </button>
                  );
                })()}

                <p className="text-center text-sm text-gray-400 dark:text-dark-text-secondary mt-6">
                  {t(
                    "subscription_note",
                    "Note: On the payment screen, you will see the exact amount and Stripe will handle billing and auto-renewal."
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      </CustomPageHeader>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title={(() => {
          const actionType = getActionType(selectedPlanForSubscription);
          switch (actionType) {
            case "upgrade":
              return t("confirm_upgrade", "Confirm Upgrade");
            case "downgrade":
              return t("confirm_downgrade", "Confirm Downgrade");
            default:
              return t("confirm_subscription", "Confirm Subscription");
          }
        })()}
        message={(() => {
          const actionType = getActionType(selectedPlanForSubscription);
          const planName =
            selectedPlanForSubscription === "yearly"
              ? t("yearly")
              : t("monthly");
          switch (actionType) {
            case "upgrade":
              return t("confirm_upgrade_message", { plan: planName });
            case "downgrade":
              return t("confirm_downgrade_message", { plan: planName });
            default:
              return t("confirm_subscription_message", { plan: planName });
          }
        })()}
        onConfirm={handleConfirmSubscription}
        onCancel={handleCancelConfirmation}
        yesLabel={(() => {
          const actionType = getActionType(selectedPlanForSubscription);
          switch (actionType) {
            case "upgrade":
              return t("yes", "Yes, Upgrade");
            case "downgrade":
              return t("yes", "Yes, Downgrade");
            default:
              return t("yes", "Yes, Subscribe");
          }
        })()}
        noLabel={t("cancel", "Cancel")}
      />
    </>
  );
};

export default SubscriptionScreen;
